import { logger } from '@/lib/logger';

const DEFAULT_PUBLIC_BACKEND_URL = 'http://localhost:5000';
const DEFAULT_INTERNAL_BACKEND_URL = 'http://cv-matcher-backend:8080';
const PRODUCTION_API_HOST = 'api.cv-matcher.com';

function derivePublicApiUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const { protocol, hostname } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//localhost:5000`;
  }

  if (hostname === PRODUCTION_API_HOST || hostname.startsWith('api.')) {
    return `${protocol}//${hostname}`;
  }

  if (
    hostname === 'cv-matcher.com' ||
    hostname === 'www.cv-matcher.com' ||
    hostname.endsWith('.cv-matcher.com')
  ) {
    return `${protocol}//${PRODUCTION_API_HOST}`;
  }

  return `${protocol}//api.${hostname}`;
}

/**
 * Centralized API URL helper class.
 * Determines the API URL once using NEXT_PUBLIC_API_URL with a fallback,
 * and provides a singleton instance for reuse throughout the application.
 *
 * For server-side API calls (SSR, API routes), prefers INTERNAL_API_URL
 * to communicate directly within the Docker network.
 */
class ApiUrlHelper {
  private static instance: ApiUrlHelper;
  private readonly apiUrl: string;
  private readonly internalApiUrl: string;
  private readonly hasExplicitInternalUrl: boolean;

  private constructor() {
    const envUrl = process.env.NEXT_PUBLIC_API_URL;
    const internalUrl = process.env.INTERNAL_API_URL;
    const defaultPublicUrl = DEFAULT_PUBLIC_BACKEND_URL;
    const defaultServerUrl = DEFAULT_INTERNAL_BACKEND_URL;
    this.hasExplicitInternalUrl = !!internalUrl;

    // On server side, prefer internal URL for Docker network communication
    if (typeof window === 'undefined' && internalUrl) {
      this.internalApiUrl = internalUrl;
    } else {
      this.internalApiUrl = internalUrl || DEFAULT_INTERNAL_BACKEND_URL;
    }

    // Prefer explicit env configuration, then derive from the current host, then fall back
    const derivedFromWindow = derivePublicApiUrl();

    const envLooksLocal =
      !!envUrl &&
      /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(envUrl);
    const derivedLooksLocal =
      !!derivedFromWindow &&
      /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(
        derivedFromWindow
      );

    // If the browser is on a public host but the bundle was built with a
    // localhost API URL, prefer the browser-derived public origin.
    if (derivedFromWindow && envLooksLocal && !derivedLooksLocal) {
      this.apiUrl = derivedFromWindow;
    } else {
      this.apiUrl = envUrl || derivedFromWindow || defaultPublicUrl;
    }

    if (typeof window === 'undefined' && !internalUrl) {
      this.internalApiUrl = defaultServerUrl;
    }

    if (!envUrl) {
      logger.warn(
        '[ApiUrlHelper] NEXT_PUBLIC_API_URL not set, using backend URL:',
        this.apiUrl
      );
    }
  }

  /**
   * Gets the singleton instance of ApiUrlHelper
   */
  static getInstance(): ApiUrlHelper {
    if (!ApiUrlHelper.instance) {
      ApiUrlHelper.instance = new ApiUrlHelper();
    }
    return ApiUrlHelper.instance;
  }

  /**
   * Gets the base API URL (determined once during initialization)
   * On server side, uses internal URL for Docker network communication.
   * On client side, uses the public URL.
   */
  getApiUrl(): string {
    if (process.env.NODE_ENV === 'test') {
      if (this.hasExplicitInternalUrl || !process.env.NEXT_PUBLIC_API_URL) {
        return this.internalApiUrl;
      }

      return this.apiUrl;
    }

    // On server side (SSR, API routes), prefer internal URL
    if (typeof window === 'undefined' && this.internalApiUrl) {
      return this.internalApiUrl;
    }
    return this.apiUrl;
  }

  /**
   * Gets the public API URL (for client-side use or external references)
   */
  getPublicApiUrl(): string {
    return this.apiUrl;
  }

  /**
   * Resets the singleton instance (useful for testing)
   */
  static reset(): void {
    ApiUrlHelper.instance = undefined as unknown as ApiUrlHelper;
  }
}

/**
 * Gets the base API URL from the centralized helper.
 * This is the single source of truth for the API URL configuration.
 *
 * On server side (SSR, API routes), returns the internal URL for Docker network communication.
 * On client side (browser), returns the public URL.
 */
export function getApiUrl(): string {
  return ApiUrlHelper.getInstance().getApiUrl();
}

/**
 * Gets the public API URL for client-side use or external references.
 * Always returns the public URL regardless of server/client context.
 */
export function getPublicApiUrl(): string {
  return ApiUrlHelper.getInstance().getPublicApiUrl();
}

function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function removeApiSuffix(url: string): string {
  if (/\/api\/v\d+$/i.test(url)) {
    return url.replace(/\/api\/v\d+$/i, '');
  }

  if (/\/api$/i.test(url)) {
    return url.replace(/\/api$/i, '');
  }

  if (/\/v\d+$/i.test(url)) {
    return url.replace(/\/v\d+$/i, '');
  }

  return url;
}

export function resolveBackendBaseUrl(): string {
  const rawBase = getApiUrl();
  const normalized = normalizeUrl(rawBase);
  return removeApiSuffix(normalized);
}

// Export the class for testing purposes
export { ApiUrlHelper };

export function resolveApiBaseUrl(): string {
  const backendBase = resolveBackendBaseUrl();
  const normalized = normalizeUrl(backendBase);

  // API versioning has been removed - ignore version parameter for backward compatibility
  // Always use non-versioned API endpoints
  if (/\/api\/v\d+$/i.test(normalized)) {
    return normalized.replace(/\/api\/v\d+$/i, '/api');
  }

  if (/\/api$/i.test(normalized)) {
    return normalized;
  }

  return `${normalized}/api`;
}

export function buildApiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${resolveApiBaseUrl()}${normalizedPath}`;
}

export function buildBackendUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${resolveBackendBaseUrl()}${normalizedPath}`;
}
